// Morning Readiness Score (Release 2 — test-user preview only).
//
// Pure compute module + a tiny hook for fetching wearable_metrics. The
// readiness score is a 0–100 summary of "how last night compares to your
// recent baseline" across the metrics your wearable actually provides.
//
// Important boundaries:
//  - This is DISPLAY ONLY. It never mutates the Menopause Impact Score
//    computed in menoScore.ts. computeReadinessAdjustment() returns a
//    visual delta the home card can render alongside today's Impact Score.
//  - Visibility is gated to profiles.is_test_user = true at the call site.
//
// Baseline window: up to 14 days of wearable_metrics rows. The baseline
// uses the prior nights only (last night excluded), and the metric must
// be present for at least MIN_BASELINE_NIGHTS prior nights AND for last
// night for that component to be scored.

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchWearableMetrics, type WearableMetricRow } from "./wearableMetrics";

export const MIN_BASELINE_NIGHTS = 7;
export const BASELINE_WINDOW_DAYS = 14;
/** Display-only Impact Score adjustment, capped at ±10 score points.
 *  Formula: round((readiness − 70) / 30 × 10), clamped to [−10, +10].
 *  Readiness above 70 nudges the displayed Impact Score UP; below 70, DOWN. */
export const IMPACT_ADJUSTMENT_CAP = 10;
export const IMPACT_ADJUSTMENT_PIVOT = 70;
export const IMPACT_ADJUSTMENT_SPAN = 30;

// ---- Readiness sub-score shape (single place to tune the "feel") ----
// An at-baseline day maps to READINESS_CENTER. Positive "oriented" deviation
// (better than your normal) adds points up to 100; negative deviation subtracts
// down to 0. READINESS_SENSITIVITY controls how sharply % deviations move the
// score — higher = sharper. Formula for every signal is:
//   sub = clamp(READINESS_CENTER + orientedPctDev * READINESS_SENSITIVITY, 0, 100)
export const READINESS_CENTER = 70;
export const READINESS_SENSITIVITY = 2.2;
// Cap positive sleep deviation so absurdly long sleep isn't rewarded past a
// reasonable ceiling (10h vs 8h baseline shouldn't peg the scale).
const SLEEP_POS_PCT_CAP = 25;
// Sleep-floor penalty applied WITHIN the centered scale (not a separate cap).
const SLEEP_FLOOR_MIN = 360;      // <6h nights are penalized
const SLEEP_FLOOR_PENALTY = 15;   // points subtracted before final clamp
// Skin temp: convert absolute °C deviation into an oriented "pct-like" value
// so it feeds the shared centered formula. 1 °C off baseline ≈ −20 units,
// i.e. a −44 pt drop at sensitivity 2.2.
const SKIN_TEMP_UNITS_PER_C = 20;


export type ReadinessBand = { label: "Recovered" | "Moderate" | "Take it easy"; color: string };
export function readinessBand(score: number): ReadinessBand {
  if (score >= 80) return { label: "Recovered", color: "#3CA37A" };
  if (score >= 60) return { label: "Moderate", color: "#C99A4D" };
  return { label: "Take it easy", color: "#D26B6B" };
}

export type ReadinessComponentKey = "sleep" | "hrv" | "rhr" | "skin_temp";

export type ReadinessComponent = {
  key: ReadinessComponentKey;
  label: string;
  score: number;           // 0–100
  lastNight: number;       // raw last-night value
  baseline: number;        // baseline mean
  delta: number;           // lastNight - baseline (raw units)
  unit: string;
  /** Number of prior nights with this metric used in baseline. */
  baselineNights: number;
};

export type ReadinessBaselineMeans = {
  sleepMinutes: { mean: number | null; nights: number };
  hrvMs: { mean: number | null; nights: number };
  restingHr: { mean: number | null; nights: number };
  skinTempC: { mean: number | null; nights: number };
};

export type ReadinessResult =
  | { state: "no_data"; baselineNightsCount: 0 }
  | {
      state: "building_baseline";
      baselineNightsCount: number; // prior nights with any metric
      needed: number;
    }
  | {
      state: "ready";
      readiness: number;            // 0–100
      components: ReadinessComponent[];
      baseline: ReadinessBaselineMeans;
      lastNightDate: string;
      baselineNightsCount: number;  // prior nights actually used (cap)
    };

function nums(values: (number | null | undefined)[]): number[] {
  return values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
}
function mean(xs: number[]): number | null {
  return xs.length ? xs.reduce((s, v) => s + v, 0) / xs.length : null;
}
function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

// Shared centered mapper: oriented % deviation → 0–100 sub-score.
// Positive orientedPctDev = "better than your normal".
function centeredSubScore(orientedPctDev: number): number {
  return clamp(READINESS_CENTER + orientedPctDev * READINESS_SENSITIVITY, 0, 100);
}

// Sleep: longer = better, up to SLEEP_POS_PCT_CAP. Very-short nights get an
// additional in-scale penalty via SLEEP_FLOOR_PENALTY (no separate 60-cap).
function scoreSleep(lastMin: number, baselineMin: number): number {
  if (baselineMin <= 0) return 0;
  const rawPct = ((lastMin - baselineMin) / baselineMin) * 100;
  const orientedPct = Math.min(rawPct, SLEEP_POS_PCT_CAP);
  let s = centeredSubScore(orientedPct);
  if (lastMin < SLEEP_FLOOR_MIN) s = clamp(s - SLEEP_FLOOR_PENALTY, 0, 100);
  return s;
}
// HRV: higher = better. Baseline maps to READINESS_CENTER, not 100.
function scoreHRV(lastMs: number, baselineMs: number): number {
  if (baselineMs <= 0) return 0;
  const orientedPct = ((lastMs - baselineMs) / baselineMs) * 100;
  return centeredSubScore(orientedPct);
}
// Resting HR: LOWER = better, so orient with (base − last).
function scoreRHR(lastBpm: number, baselineBpm: number): number {
  if (baselineBpm <= 0) return 0;
  const orientedPct = ((baselineBpm - lastBpm) / baselineBpm) * 100;
  return centeredSubScore(orientedPct);
}
// Skin temp: deviation in EITHER direction is worse. Convert |Δ°C| into
// the same oriented-pct space via SKIN_TEMP_UNITS_PER_C, always negative.
function scoreSkinTemp(lastC: number, baselineC: number): number {
  const orientedPct = -Math.abs(lastC - baselineC) * SKIN_TEMP_UNITS_PER_C;
  return centeredSubScore(orientedPct);
}


// ---------- per-date helpers ----------
//
// When multiple providers report on the same metric_date, pick ONE value per
// metric per date using the same "richest row wins" rule the home Wearable
// Snapshot card uses. Never average across providers.

// Match the home Wearable Snapshot card's richness set so the same provider
// row wins ties for any given date+metric. (Oura's sleep_quality_score is
// what makes its 06/11 row richer than Fitbit's, for example.)
const RICHNESS_FIELDS: (keyof WearableMetricRow)[] = [
  "sleep_duration_minutes",
  "sleep_quality_score",
  "hrv_ms",
  "resting_heart_rate",
  "skin_temperature_c",
  "steps",
  "active_minutes",
  "vo2_max",
];
function richness(r: WearableMetricRow): number {
  let n = 0;
  for (const f of RICHNESS_FIELDS) if (r[f] != null) n++;
  return n;
}
function groupByDate(rows: WearableMetricRow[]): Map<string, WearableMetricRow[]> {
  const m = new Map<string, WearableMetricRow[]>();
  for (const r of rows) {
    const arr = m.get(r.metric_date) ?? [];
    arr.push(r);
    m.set(r.metric_date, arr);
  }
  return m;
}
function pickValueForDate(
  rowsForDate: WearableMetricRow[],
  field: keyof WearableMetricRow,
): number | null {
  const cands = rowsForDate.filter((r) => r[field] != null);
  if (!cands.length) return null;
  let best = cands[0];
  let bs = richness(best);
  for (let i = 1; i < cands.length; i++) {
    const s = richness(cands[i]);
    if (s > bs) { best = cands[i]; bs = s; }
  }
  return best[field] as number;
}

/** Require at least this many scored components for a "ready" reading. */
export const MIN_COMPONENTS = 2;

/** Core: compute readiness for a specific target date using rows that may
 *  span many dates. Baseline = the up-to-BASELINE_WINDOW_DAYS distinct
 *  metric_dates strictly before targetDate, one value per date per metric. */
export function computeReadinessForDate(
  rows: WearableMetricRow[],
  targetDate: string,
): ReadinessResult {
  const byDate = groupByDate(rows);
  const todayRows = byDate.get(targetDate);
  if (!todayRows || !todayRows.length) {
    return { state: "no_data", baselineNightsCount: 0 };
  }

  const priorDates = [...byDate.keys()]
    .filter((d) => d < targetDate)
    .sort()
    .slice(-BASELINE_WINDOW_DAYS);

  if (priorDates.length < MIN_BASELINE_NIGHTS) {
    return {
      state: "building_baseline",
      baselineNightsCount: priorDates.length,
      needed: MIN_BASELINE_NIGHTS,
    };
  }

  const pickBaseline = (field: keyof WearableMetricRow): number[] => {
    const out: number[] = [];
    for (const d of priorDates) {
      const v = pickValueForDate(byDate.get(d)!, field);
      if (v != null) out.push(v);
    }
    return out;
  };
  const sleepBase = pickBaseline("sleep_duration_minutes");
  const hrvBase = pickBaseline("hrv_ms");
  const rhrBase = pickBaseline("resting_heart_rate");
  const tempBase = pickBaseline("skin_temperature_c");

  const baseline: ReadinessBaselineMeans = {
    sleepMinutes: { mean: mean(sleepBase), nights: sleepBase.length },
    hrvMs: { mean: mean(hrvBase), nights: hrvBase.length },
    restingHr: { mean: mean(rhrBase), nights: rhrBase.length },
    skinTempC: { mean: mean(tempBase), nights: tempBase.length },
  };

  const lastSleep = pickValueForDate(todayRows, "sleep_duration_minutes");
  const lastHrv = pickValueForDate(todayRows, "hrv_ms");
  const lastRhr = pickValueForDate(todayRows, "resting_heart_rate");
  const lastTemp = pickValueForDate(todayRows, "skin_temperature_c");

  // Per-metric gating: require the metric for last night AND a non-null
  // baseline mean (≥1 prior night with that metric). The overall
  // "≥7 prior nights" gate is enforced above on the distinct-dates count;
  // we deliberately do NOT also require ≥7 prior nights PER metric, which
  // would falsely fail dates whose 14-day window has metric gaps.
  const components: ReadinessComponent[] = [];
  if (lastSleep != null && baseline.sleepMinutes.mean != null) {
    const base = baseline.sleepMinutes.mean;
    components.push({
      key: "sleep", label: "Sleep duration",
      score: Math.round(scoreSleep(lastSleep, base)),
      lastNight: lastSleep, baseline: Math.round(base),
      delta: Math.round(lastSleep - base), unit: "min",
      baselineNights: baseline.sleepMinutes.nights,
    });
  }
  if (lastHrv != null && baseline.hrvMs.mean != null) {
    const base = baseline.hrvMs.mean;
    components.push({
      key: "hrv", label: "HRV",
      score: Math.round(scoreHRV(lastHrv, base)),
      lastNight: round1(lastHrv), baseline: round1(base),
      delta: round1(lastHrv - base), unit: "ms",
      baselineNights: baseline.hrvMs.nights,
    });
  }
  if (lastRhr != null && baseline.restingHr.mean != null) {
    const base = baseline.restingHr.mean;
    components.push({
      key: "rhr", label: "Resting HR",
      score: Math.round(scoreRHR(lastRhr, base)),
      lastNight: lastRhr, baseline: Math.round(base),
      delta: Math.round(lastRhr - base), unit: "bpm",
      baselineNights: baseline.restingHr.nights,
    });
  }
  if (lastTemp != null && baseline.skinTempC.mean != null) {
    const base = baseline.skinTempC.mean;
    components.push({
      key: "skin_temp", label: "Skin temperature",
      score: Math.round(scoreSkinTemp(lastTemp, base)),
      lastNight: round1(lastTemp), baseline: round1(base),
      delta: round1(lastTemp - base), unit: "°C",
      baselineNights: baseline.skinTempC.nights,
    });
  }

  if (components.length < MIN_COMPONENTS) {
    return {
      state: "building_baseline",
      baselineNightsCount: priorDates.length,
      needed: MIN_BASELINE_NIGHTS,
    };
  }

  const readiness = Math.round(
    components.reduce((s, c) => s + c.score, 0) / components.length,
  );

  return {
    state: "ready",
    readiness,
    components,
    baseline,
    lastNightDate: targetDate,
    baselineNightsCount: priorDates.length,
  };
}

export function computeReadinessScore(rows: WearableMetricRow[]): ReadinessResult {
  if (!rows.length) return { state: "no_data", baselineNightsCount: 0 };
  const dates = [...new Set(rows.map((r) => r.metric_date))].sort();
  const target = dates[dates.length - 1];
  return computeReadinessForDate(rows, target);
}

/** Compute the display-only Impact Score adjustment.
 *  adjustment = round((readiness − 70) / 30 × 10), clamped to [−10, +10].
 *  Readiness above 70 nudges the displayed Impact Score UP; below 70, DOWN. */
export function computeReadinessAdjustment(
  impactScore: number | null,
  result: ReadinessResult,
): { adjustedScore: number; delta: number; readiness: number } | null {
  if (impactScore == null || result.state !== "ready") return null;
  const raw = ((result.readiness - IMPACT_ADJUSTMENT_PIVOT) / IMPACT_ADJUSTMENT_SPAN) * 10;
  const delta = clamp(Math.round(raw), -IMPACT_ADJUSTMENT_CAP, IMPACT_ADJUSTMENT_CAP);
  const adjusted = clamp(Math.round(impactScore + delta), 0, 100);
  return { adjustedScore: adjusted, delta, readiness: result.readiness };
}

// ---------- 14-day series ----------

export type ReadinessSeriesReason =
  | "no_data"
  | "insufficient_baseline"
  | "insufficient_components";

export type ReadinessSeriesPoint = {
  date: string; // YYYY-MM-DD
  score: number | null;
  band: ReadinessBand["label"] | null;
  reason?: ReadinessSeriesReason;
};

function ymd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Build a 14-day (or N-day) readiness series ending at `endDate` (default
 *  = today, UTC). Caller passes ONE pre-fetched window of wearable rows
 *  covering at least `days + BASELINE_WINDOW_DAYS` calendar days back. */
export function computeReadinessSeries(
  rows: WearableMetricRow[],
  days = 14,
  endDate: Date = new Date(),
): ReadinessSeriesPoint[] {
  const out: ReadinessSeriesPoint[] = [];
  const end = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()));
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setUTCDate(end.getUTCDate() - i);
    const date = ymd(d);
    const res = computeReadinessForDate(rows, date);
    if (res.state === "ready") {
      out.push({ date, score: res.readiness, band: readinessBand(res.readiness).label });
    } else if (res.state === "no_data") {
      out.push({ date, score: null, band: null, reason: "no_data" });
    } else {
      // building_baseline covers both "<7 prior nights" and "<2 components"
      const reason: ReadinessSeriesReason =
        res.baselineNightsCount < MIN_BASELINE_NIGHTS
          ? "insufficient_baseline"
          : "insufficient_components";
      out.push({ date, score: null, band: null, reason });
    }
  }
  return out;
}

// ---------- hook ----------

export type UseReadinessScoreState = {
  loading: boolean;
  enabled: boolean;
  result: ReadinessResult | null;
  series: ReadinessSeriesPoint[];
};

/** Fetches a single window covering both today's reading and the 14-day
 *  series, then computes both client-side. Returns `{ enabled: false }`
 *  synchronously when the flag is off so non-test users do zero network work. */
export function useReadinessScore(isTestUser: boolean | null | undefined): UseReadinessScoreState {
  const [state, setState] = useState<UseReadinessScoreState>({
    loading: !!isTestUser,
    enabled: !!isTestUser,
    result: null,
    series: [],
  });

  useEffect(() => {
    if (!isTestUser) {
      setState({ loading: false, enabled: false, result: null, series: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const uid = data?.user?.id;
        if (!uid) {
          if (!cancelled) setState({ loading: false, enabled: true, result: null, series: [] });
          return;
        }
        // One query covers both today's reading and the trailing 14-day series.
        const rows = await fetchWearableMetrics(uid, 14 + BASELINE_WINDOW_DAYS);
        const result = computeReadinessScore(rows);
        const series = computeReadinessSeries(rows, 14);
        if (!cancelled) setState({ loading: false, enabled: true, result, series });
      } catch (e) {
        console.warn("[readinessScore] fetch failed", e);
        if (!cancelled) setState({ loading: false, enabled: true, result: null, series: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [isTestUser]);

  return state;
}

